import axios from 'src/utils/axios';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useNotify } from '../useNotify';
import { FormSchema } from '../../models/schema';
import { schoolFields } from '../../models/school';

const initialParams = {
  pagesize: 10,
  page: 0
};

export const useSchools = (idState) => {
  const [schools, setSchools] = useState([]);
  const [totalSchool, setTotalSchool] = useState(0);
  const [idSchool, setIdSchool] = useState('');
  const [error, setError] = useState();
  const [params, setParams] = useState(initialParams);

  const [schoolList, setSchoolList] = useState([]);
  const [errorSchoolList, setErrorSchoolList] = useState();
  const [typeOfSchoolList, setTypeOfSchoolList] = useState([]);
  const [errorTypeOfSchoolList, setErrorTypeOfSchoolList] = useState();

  const [schoolListAll, setSchoolListAll] = useState([]);

  const [open, setOpen] = useState(false);
  const [openModalSchool, setOpenModalSchool] = useState(false);

  const { t } = useTranslation();
  const { success, error: errorMsg } = useNotify();

  const { schema } = useMemo(
    () =>
      new FormSchema(schoolFields.id, [
        schoolFields.name,
        schoolFields.code,
        schoolFields.country,
        schoolFields.state,
        schoolFields.typeOfSchool
      ]),
    []
  );

  const { handleSubmit, control, formState, watch, reset, setValue } = useForm({
    defaultValues: {
      [schoolFields.country.name]: '',
      [schoolFields.state.name]: '',
      [schoolFields.typeOfSchool.name]: '',
      [schoolFields.name.name]: '',
      [schoolFields.code.name]: ''
    },
    resolver: schema
  });

  const isSubmitting = useMemo(() => formState.isSubmitting, [formState]);

  const formItems = useMemo(
    () => ({
      control,
      formState,
      fullWidth: true
    }),
    [control, formState]
  );

  const onSubmit = useCallback(async (formData) => {
    try {
      if (idSchool) {
        await axios.patch(`admin/school/${idSchool}`, formData);
        success(t('actions.updateSuccess', { name: schoolFields.school.label }));
      } else {
        await axios.post('admin/school', formData);
        success(t('actions.createSuccess', { name: schoolFields.school.label }));
      }
      reset();
      fetchSchools();
      setOpenModalSchool(false);
    } catch (e) {
      console.log(e);
      if (idSchool) {
        errorMsg(t('actions.updateFailed', { name: schoolFields.school.label }));
      } else {
        errorMsg(t('actions.createFailed', { name: schoolFields.school.label }));
      }
    }
  });

  const fetchSchools = async () => {
    try {
      const { data } = await axios.get(`/admin/school?pagesize=${params.pagesize}&page=${params.page + 1}`);
      setSchools(data?.data?.schools);
      setError({ success: data?.isSuccess, error: data?.msg });
      setTotalSchool(data?.data?.totalSchool);
    } catch (e) {
      console.log(e);
      setError(e);
    }
  };

  const fetchSchoolList = async (id) => {
    try {
      const { data } = await axios.get(`/admin/schoollist/${id}`);
      const formatSchoolList = data?.data.map((school) => ({
        value: school._id,
        label: school.name,
        code: school.code
      }));
      setSchoolList(formatSchoolList);
    } catch (e) {
      console.log(e);
      setErrorSchoolList(e);
    }
  };

  const fetchSchoolListAll = async () => {
    try {
      const { data } = await axios.get(`/admin/schoollist/0`);
      const formatSchoolList = data?.data.map((school) => ({
        id: school._id,
        value: school._id,
        label: school.name,
        code: school.code
      }));
      formatSchoolList.unshift({ id: 0, value: 0, label: 'All' });
      setSchoolListAll(formatSchoolList);
    } catch (e) {
      console.log(e);
      setErrorSchoolList(e);
    }
  };

  const fetchTypeOfSchoolList = async () => {
    try {
      const { data } = await axios.get('/admin/typeofschool');
      const formatTypeOfSchoolList = data?.data.map((school) => ({ value: school._id, label: school.name }));
      setTypeOfSchoolList(formatTypeOfSchoolList);
    } catch (e) {
      console.log(e);
      setErrorTypeOfSchoolList(e);
    }
  };
  const removeSchool = async (id) => {
    try {
      await axios.delete(`/admin/school/${id}`);
      success(t('actions.removeSuccess', { name: schoolFields.school.label }));
      fetchSchools();
    } catch (e) {
      errorMsg(t('actions.removeFailed', { name: schoolFields.school.label }));
      console.log(e);
    }
  };

  const handleLimitChange = useCallback((event) => {
    setParams((pre) => ({
      ...pre,
      pagesize: parseInt(event.target.value, 10),
      page: 0
    }));
  }, []);

  const handleOnPageChange = (event, newPage) => {
    setParams((pre) => ({
      ...pre,
      page: newPage
    }));
  };

  const onOpenModal = useCallback((isOpen) => {
    setOpen(isOpen);
  }, []);

  const onOpenModalSchool = useCallback((isOpen) => {
    setOpenModalSchool(isOpen);
    reset();
  }, []);

  useEffect(() => {
    if (idState) {
      fetchSchoolList(idState);
    }
  }, [idState]);

  useEffect(() => {
    fetchTypeOfSchoolList(idState);
  }, []);
  useEffect(() => {
    fetchSchoolListAll();
  }, []);

  useEffect(() => {
    fetchSchools();
  }, [params]);

  return {
    schools,
    setParams,
    params,
    error,
    handleOnPageChange,
    handleLimitChange,
    totalSchool,
    removeSchool,
    open,
    onOpenModal,
    onOpenModalSchool,
    openModalSchool,
    setValue,
    setIdSchool,
    idSchool,
    handleSubmit,
    isSubmitting,
    formItems,
    control,
    watch,
    onSubmit,
    schoolList,
    errorSchoolList,
    errorTypeOfSchoolList,
    typeOfSchoolList,
    schoolListAll
  };
};
